import { Button, Form, Modal } from "react-bootstrap";
import { Card, Option } from "../../api/tooling";
import {
  ApprovalBadge,
  DeleteCross,
  dateLabel,
  ErrorNotice,
  MakerBadge,
} from "./ToolingShared";
import CardPrint from "./CardPrint";

export function drawingUrl(value?: string | null): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export default function CardDetails({
  card,
  issues,
  statuses,
  canEdit,
  busy,
  error,
  onClose,
  onEdit,
  onMove,
  onAudit,
  canApprove,
  onApprove,
  onDelete,
  onRejectDeletion,
}: {
  card: Card;
  issues: Option[];
  statuses: Option[];
  canEdit: boolean;
  busy: boolean;
  error?: unknown;
  onClose: () => void;
  onEdit: () => void;
  onMove: (status: string) => void;
  onAudit?: () => void;
  canApprove: boolean;
  onApprove: () => void;
  onDelete: () => void;
  onRejectDeletion: () => void;
}) {
  return (
    <Modal
      show
      onHide={onClose}
      size="lg"
      scrollable
      aria-labelledby="card-detail-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="card-detail-title">
          {card.tool.code} · {card.tool.parts[0]?.part_name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorNotice error={error} />
        <ApprovalBadge card={card} />
        <CardPrint card={card} issues={issues} statuses={statuses} />
        <div className="tool-detail-meta">
          <span>Card #{card.id}</span>
          <span>{issues.find((i) => i.value === card.issue_type)?.label}</span>
          <span>{card.points} points</span>
        </div>
        <h6 className="mt-4">Details</h6>
        <p className="tooling-detail">{card.detail}</p>
        <h6 className="mt-4">Tooling details</h6>
        <p className="tooling-detail">{card.tool.details || "—"}</p>
        <h6 className="mt-4">Die makers</h6>
        <div className="d-flex flex-wrap gap-2">
          {card.makers.map((m) => (
            <MakerBadge key={m.id} maker={m} />
          ))}
        </div>
        <h6 className="mt-4">Linked parts & drawings</h6>
        <ul className="tool-drawing-list">
          {card.tool.parts.map((p) => (
            <li key={p.part_id}>
              <span>
                {p.part_name} <small className="text-muted">#{p.part_id}</small>
                {p.drawing_name && (
                  <small className="d-block">Drawing: {p.drawing_name}</small>
                )}
              </span>
              {drawingUrl(p.drawing_link) ? (
                <a
                  className="btn btn-sm btn-outline-primary"
                  href={drawingUrl(p.drawing_link)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i
                    className="bi bi-box-arrow-up-right me-2"
                    aria-hidden="true"
                  />
                  Drawing
                </a>
              ) : (
                <small className="text-muted">No drawing linked</small>
              )}
            </li>
          ))}
        </ul>
        <div className="tool-detail-meta mt-3">
          <span>Created {dateLabel(card.created_at)}</span>
          <span>Completed {dateLabel(card.completed_at)}</span>
        </div>
        <Form.Group className="mt-4" controlId="detail-stage">
          <Form.Label>Stage</Form.Label>
          <Form.Select
            value={card.status}
            disabled={
              !canEdit || busy || card.deletion_pending || card.is_deleted
            }
            onChange={(e) => onMove(e.target.value)}
          >
            {statuses.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button className="tooling-print-button" onClick={() => window.print()}>
          Print card
        </Button>
        {canApprove &&
          !card.approved &&
          !card.deletion_pending &&
          !card.is_deleted && (
            <Button variant="success" disabled={busy} onClick={onApprove}>
              Approve card
            </Button>
          )}
        {canApprove && card.deletion_pending && !card.is_deleted && (
          <Button
            variant="outline-secondary"
            disabled={busy}
            onClick={onRejectDeletion}
          >
            Reject deletion
          </Button>
        )}
        {canEdit &&
          !card.is_deleted &&
          (!card.deletion_pending || canApprove) && (
            <Button
              className="tooling-delete-cross"
              variant="outline-danger"
              disabled={busy}
              onClick={onDelete}
              title={
                card.deletion_pending
                  ? "Approve deletion"
                  : canApprove
                  ? "Delete card"
                  : "Request deletion"
              }
              aria-label={
                card.deletion_pending
                  ? "Approve deletion"
                  : canApprove
                  ? "Delete card"
                  : "Request deletion"
              }
            >
              <DeleteCross />
            </Button>
          )}
        {onAudit && (
          <Button className="tooling-audit-button" onClick={onAudit}>
            Change log
          </Button>
        )}
        {canEdit && !card.is_deleted && (
          <Button
            className="btn-action"
            disabled={busy || card.deletion_pending}
            onClick={onEdit}
          >
            Edit card
          </Button>
        )}
        <Button variant="light" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
